/**
 * Export Utilities for Data Export
 * 
 * Provides functions to export sensor and actuator data in various formats
 */

import { fetchHistoricalSensorData } from './mqttUtils';

/**
 * Convert data to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {string} filename - Name for the downloaded file
 */
export function exportToCSV(data, filename = 'export.csv') {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvHeaders = headers.join(',');
  const csvRows = data.map(row => {
    return headers.map(header => {
      const value = row[header];
      // Handle nested objects and arrays
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value).replace(/"/g, '""');
      }
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });

  const csvContent = [csvHeaders, ...csvRows].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convert data to JSON format
 * @param {Array|Object} data - Data to convert
 * @param {string} filename - Name for the downloaded file
 */
export function exportToJSON(data, filename = 'export.json') {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export all cards (sensors) data
 * @param {Array} cards - Array of card objects
 * @param {string} format - 'csv' or 'json'
 */
export async function exportAllCards(cards, format = 'json') {
  if (!cards || cards.length === 0) {
    alert('No sensor data available to export');
    return;
  }

  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `all-sensors-${timestamp}.${format}`;
  
  if (format === 'csv') {
    exportToCSV(cards, filename);
  } else {
    exportToJSON(cards, filename);
  }
}

/**
 * Export all actions (actuators) data
 * @param {Array} actions - Array of action objects
 * @param {string} format - 'csv' or 'json'
 */
export async function exportAllActions(actions, format = 'json') {
  if (!actions || actions.length === 0) {
    alert('No actuator data available to export');
    return;
  }

  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `all-actuators-${timestamp}.${format}`;
  
  if (format === 'csv') {
    exportToCSV(actions, filename);
  } else {
    exportToJSON(actions, filename);
  }
}

/**
 * Export a single sensor with its historical data
 * @param {Object} card - Card/sensor object
 * @param {string} format - 'csv' or 'json'
 * @param {string} timeRange - Time range for historical data (e.g., '30d', '7d')
 */
export async function exportIndividualSensor(card, format = 'json', timeRange = '14d') {
  if (!card) {
    alert('No sensor selected');
    return;
  }

  try {
    // Show loading message since this may take a moment for large datasets
    console.log(`Fetching ${timeRange} of historical data from InfluxDB for sensor: ${card.macAddress}`);
    
    // Fetch historical data for all sensor types from InfluxDB (same as chart queries)
    const sensorTypes = ['humidity', 'temperature', 'distance', 'lux'];
    const historicalData = {};

    // Fetch all sensor data in parallel from InfluxDB
    await Promise.all(
      sensorTypes.map(async (type) => {
        try {
          // Use the same fetchHistoricalSensorData function that charts use
          // This queries InfluxDB with the same Flux query format
          const data = await fetchHistoricalSensorData(
            card.macAddress,
            type,
            timeRange,
            'sensor', // deviceType: 'sensor' uses the sensor measurement
            '5m' // aggregateWindow: 5-minute aggregation (same as charts)
          );
          historicalData[type] = data;
          console.log(`Fetched ${data.length} data points for ${type} over ${timeRange}`);
        } catch (error) {
          console.error(`Error fetching ${type} data from InfluxDB:`, error);
          historicalData[type] = [];
        }
      })
    );

    // Combine card data with historical data
    const exportData = {
      sensor: {
        ...card,
        exportedAt: new Date().toISOString(),
        timeRange: timeRange
      },
      historicalData: historicalData
    };

    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedName = (card.sensorDescription || card.macAddress || 'sensor')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    const filename = `sensor-${sanitizedName}-${timestamp}.${format}`;

    if (format === 'csv') {
      // For CSV, flatten the structure
      const flattenedData = [
        {
          macAddress: card.macAddress,
          sensorDescription: card.sensorDescription,
          exportedAt: exportData.sensor.exportedAt,
          timeRange: timeRange,
          humidityLow: card.humidityLow,
          humidityHigh: card.humidityHigh,
          temperatureLow: card.temperatureLow,
          temperatureHigh: card.temperatureHigh,
          waterLow: card.waterLow,
          waterHigh: card.waterHigh,
          lightLow: card.lightLow,
          lightHigh: card.lightHigh
        }
      ];
      
      // Add historical data rows
      const maxLength = Math.max(
        ...Object.values(historicalData).map(arr => arr.length)
      );
      
      for (let i = 0; i < maxLength; i++) {
        const row = {
          timestamp: '',
          humidity: '',
          temperature: '',
          distance: '',
          lux: ''
        };
        
        sensorTypes.forEach(type => {
          if (historicalData[type] && historicalData[type][i]) {
            row.timestamp = historicalData[type][i].timestamp || '';
            row[type] = historicalData[type][i].value || '';
          }
        });
        
        flattenedData.push(row);
      }
      
      exportToCSV(flattenedData, filename);
    } else {
      exportToJSON(exportData, filename);
    }
  } catch (error) {
    console.error('Error exporting sensor data:', error);
    alert('Failed to export sensor data. Please try again.');
  }
}

/**
 * Export a single actuator with its historical data
 * @param {Object} action - Action/actuator object
 * @param {string} format - 'csv' or 'json'
 * @param {string} timeRange - Time range for historical data (e.g., '30d', '7d')
 */
export async function exportIndividualActuator(action, format = 'json', timeRange = '14d') {
  if (!action) {
    alert('No actuator selected');
    return;
  }

  try {
    // Show loading message since this may take a moment for large datasets
    console.log(`Fetching ${timeRange} of historical data from InfluxDB for actuator: ${action.macAddress}`);
    
    // Fetch historical status data for the actuator from InfluxDB (same as chart queries)
    const historicalData = await fetchHistoricalSensorData(
      action.macAddress,
      'status',
      timeRange,
      'action', // deviceType: 'action' uses the action measurement
      '5m' // aggregateWindow: 5-minute aggregation (same as charts)
    );
    
    console.log(`Fetched ${historicalData.length} status data points over ${timeRange}`);

    // Combine action data with historical data
    const exportData = {
      actuator: {
        ...action,
        exportedAt: new Date().toISOString(),
        timeRange: timeRange
      },
      historicalData: {
        status: historicalData
      }
    };

    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedName = (action.actuatorDescription || action.macAddress || 'actuator')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    const filename = `actuator-${sanitizedName}-${timestamp}.${format}`;

    if (format === 'csv') {
      // For CSV, flatten the structure
      const flattenedData = [
        {
          macAddress: action.macAddress,
          actuatorDescription: action.actuatorDescription,
          actuatorType: action.actuatorType,
          sensor: action.sensor,
          status: action.status,
          tracking: action.tracking,
          pwm: action.pwm,
          duration: action.duration,
          sensorTracking: action.sensorTracking,
          exportedAt: exportData.actuator.exportedAt,
          timeRange: timeRange
        },
        ...historicalData.map(item => ({
          timestamp: item.timestamp || '',
          status: item.value || '',
          macAddress: '',
          actuatorDescription: '',
          actuatorType: '',
          sensor: '',
          tracking: '',
          pwm: '',
          duration: '',
          sensorTracking: ''
        }))
      ];
      
      exportToCSV(flattenedData, filename);
    } else {
      exportToJSON(exportData, filename);
    }
  } catch (error) {
    console.error('Error exporting actuator data:', error);
    alert('Failed to export actuator data. Please try again.');
  }
}

/**
 * Export all data (cards + actions + historical data summary)
 * @param {Array} cards - Array of card objects
 * @param {Array} actions - Array of action objects
 * @param {string} format - 'csv' or 'json'
 * @param {string} timeRange - Time range for historical data summary
 */
export async function exportAllData(cards, actions, format = 'json', timeRange = '7d') {
  if ((!cards || cards.length === 0) && (!actions || actions.length === 0)) {
    alert('No data available to export');
    return;
  }

  try {
    const exportData = {
      exportInfo: {
        exportedAt: new Date().toISOString(),
        timeRange: timeRange,
        totalSensors: cards?.length || 0,
        totalActuators: actions?.length || 0
      },
      sensors: cards || [],
      actuators: actions || []
    };

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `all-data-${timestamp}.${format}`;

    if (format === 'csv') {
      // For CSV, export sensors and actuators separately or combined
      const allData = [
        ...(cards || []).map(card => ({
          type: 'sensor',
          macAddress: card.macAddress,
          description: card.sensorDescription,
          humidityLow: card.humidityLow,
          humidityHigh: card.humidityHigh,
          temperatureLow: card.temperatureLow,
          temperatureHigh: card.temperatureHigh,
          waterLow: card.waterLow,
          waterHigh: card.waterHigh,
          lightLow: card.lightLow,
          lightHigh: card.lightHigh
        })),
        ...(actions || []).map(action => ({
          type: 'actuator',
          macAddress: action.macAddress,
          description: action.actuatorDescription,
          actuatorType: action.actuatorType,
          sensor: action.sensor,
          status: action.status,
          tracking: action.tracking,
          pwm: action.pwm,
          duration: action.duration,
          sensorTracking: action.sensorTracking
        }))
      ];
      
      exportToCSV(allData, filename);
    } else {
      exportToJSON(exportData, filename);
    }
  } catch (error) {
    console.error('Error exporting all data:', error);
    alert('Failed to export data. Please try again.');
  }
}

